import { getSkillNamesForTest, isDiagnostic } from "@helpers/exams";
import { formatList } from "@memorang/helpers/i18n";
import { examCoversSkill } from "@memorang/helpers/language-learning";
import type { MixedItemTag, PracticeTestExam } from "@memorang/types";
import type { TasksSectionItem } from "@memorang/ui/components/HomeApplet/types";
import type { TFunction } from "i18next";
import {
	PRACTICE_TEST_COOLDOWN_DAYS,
	SECTIONAL_TEST_COOLDOWN_DAYS,
	buildTestItem,
	filterFullPracticeTests,
	filterSectionalTests,
	getNextEligibleTest,
	getRecentActiveTest,
	getRecentCompletedTest,
	pickRecommendedDiagnostics,
} from "./helpers";
import { isSectionalTest } from "./test-kinds";

type TestTaskContext = Omit<
	Parameters<typeof buildTestItem>[0],
	"test" | "status" | "body"
>;

const optionalTestTask = (
	test: PracticeTestExam | undefined,
	input: TestTaskContext & {
		status: "ACTIVE" | "COMPLETE" | "NOT_STARTED";
		body: string;
	},
): TasksSectionItem[] => (test ? [buildTestItem({ ...input, test })] : []);

/**
 * Skills a test "claims" as its own top recommendation. Only diagnostics and
 * sectionals claim — a full mock or the completed-review card never displaces
 * a skill's study task, even if its title or tags happen to name one.
 */
const skillIdsOwnedBy = (
	test: PracticeTestExam,
	skills: MixedItemTag[],
): number[] =>
	isDiagnostic(test) || isSectionalTest(test)
		? skills
				.filter((skill) => examCoversSkill(test, skill))
				.map((skill) => skill.id)
		: [];

export const buildRecommendedTestTasks = ({
	allTests,
	practiceTests,
	diagnostics,
	skills,
	preferredSkillIds,
	diagnosticLabel,
	t,
	taskContext,
}: {
	allTests: PracticeTestExam[];
	practiceTests: PracticeTestExam[];
	diagnostics: PracticeTestExam[];
	skills: MixedItemTag[];
	preferredSkillIds?: string[];
	diagnosticLabel: string;
	t: TFunction;
	taskContext: TestTaskContext;
}): {
	tasks: TasksSectionItem[];
	coveredSkillIds: number[];
} => {
	const claimed = new Set<number>();
	const isRedundant = (test: PracticeTestExam): boolean => {
		const skillIds = skillIdsOwnedBy(test, skills);
		return skillIds.length > 0 && skillIds.every((id) => claimed.has(id));
	};
	const claim = (test: PracticeTestExam): void => {
		for (const id of skillIdsOwnedBy(test, skills)) {
			claimed.add(id);
		}
	};

	const active = getRecentActiveTest(allTests);
	if (active) {
		claim(active);
	}
	const completed = getRecentCompletedTest(allTests);

	const recommendedDiagnostics: PracticeTestExam[] = [];
	for (const test of pickRecommendedDiagnostics({
		diagnostics,
		skills,
		preferredSkillIds,
	})) {
		// An active diagnostic already has its resume card above.
		if (test.id !== active?.id && !isRedundant(test)) {
			claim(test);
			recommendedDiagnostics.push(test);
		}
	}

	// New practice tests wait until nothing is in progress. A claimed
	// not-started sectional is skipped in favor of one for an unclaimed skill;
	// completed sectionals stay in the pool so the cooldown baseline holds.
	const sectional = active
		? undefined
		: getNextEligibleTest({
				tests: filterSectionalTests(practiceTests).filter(
					(test) => test.status !== "NOT_STARTED" || !isRedundant(test),
				),
				cooldownDays: SECTIONAL_TEST_COOLDOWN_DAYS,
			});
	if (sectional) {
		claim(sectional);
	}

	const fullMock = active
		? undefined
		: getNextEligibleTest({
				tests: filterFullPracticeTests(practiceTests),
				cooldownDays: PRACTICE_TEST_COOLDOWN_DAYS,
			});

	return {
		tasks: [
			...optionalTestTask(active, {
				...taskContext,
				status: "ACTIVE",
				body: t("dashboard.recommendedTasks.activeTestBody"),
			}),
			...optionalTestTask(completed, {
				...taskContext,
				status: "COMPLETE",
				body: t("dashboard.recommendedTasks.completedTestBody"),
			}),
			...recommendedDiagnostics.map((test) =>
				buildTestItem({
					...taskContext,
					test,
					status: test.status === "ACTIVE" ? "ACTIVE" : "NOT_STARTED",
					body: t("dashboard.recommendedTasks.diagnosticBody", {
						label: diagnosticLabel,
					}),
				}),
			),
			...(sectional
				? optionalTestTask(sectional, {
						...taskContext,
						status: "NOT_STARTED",
						body: t("dashboard.recommendedTasks.sectionalTestBody", {
							skills: formatList(
								undefined,
								getSkillNamesForTest(sectional, skills),
							),
						}),
					})
				: []),
			...optionalTestTask(fullMock, {
				...taskContext,
				status: "NOT_STARTED",
				body: t("dashboard.recommendedTasks.practiceTestBody"),
			}),
		],
		coveredSkillIds: [...claimed],
	};
};
